import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Mail, School, ChevronRight } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
type Role = 'learner' | 'parent' | 'teacher';
interface RegisterDetailsStepProps {
  role: Role;
  grade: string;
  onGradeChange: (grade: string) => void;
  email: string;
  onEmailChange: (email: string) => void;
  schoolName: string;
  onSchoolNameChange: (name: string) => void;
  schoolSearch: string;
  onSchoolSearchChange: (search: string) => void;
  schoolInputMode: 'search' | 'manual';
  onSchoolInputModeChange: (mode: 'search' | 'manual') => void;
  schoolSelected: boolean;
  onSchoolSelected: (selected: boolean) => void;
  existingSchools: string[];
  onNext: () => void;
  slideVariants: Record<string, unknown>;
}
export function RegisterDetailsStep({
  role,
  grade,
  onGradeChange,
  email,
  onEmailChange,
  schoolName,
  onSchoolNameChange,
  schoolSearch,
  onSchoolSearchChange,
  schoolInputMode,
  onSchoolInputModeChange,
  schoolSelected,
  onSchoolSelected,
  existingSchools,
  onNext,
  slideVariants
}: RegisterDetailsStepProps) {
  const itemVariants = {
    enter: {
      opacity: 0,
      y: 20
    },
    center: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      y: -20
    }
  };
  // Sprint 2.6 (school-dedupe): normalize a school name for fuzzy comparison.
  // Strips common Dutch school prefixes and collapses whitespace so that
  // "obs De Vlieger" / "De Vlieger" / "basisschool de vlieger" all collide.
  function normalizeSchoolName(raw: string): string {
    return raw.toLowerCase().trim().replace(/^(obs|cbs|pcbs|rkbs|rks|bs|basisschool|school|kbs|sbo|ibs)\s+/, '').replace(/\s+/g, ' ').trim();
  }
  function findSchoolSuggestion(typed: string, existing: string[]): string | null {
    const normTyped = normalizeSchoolName(typed);
    if (!normTyped || normTyped.length < 3) return null;
    // Exact match (after normalisation) → don't suggest, the user will keep their casing.
    const exact = existing.find(s => normalizeSchoolName(s) === normTyped && s !== typed.trim());
    if (exact) return exact;
    // Substring match either direction
    const sub = existing.find(s => {
      const ns = normalizeSchoolName(s);
      if (ns === normTyped) return false;
      return ns.includes(normTyped) || normTyped.includes(ns);
    });
    return sub ?? null;
  }
  return <motion.div key="step-details" custom={1} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
    duration: 0.3,
    staggerChildren: 0.1
  }} className="space-y-5" data-id="element-336" data-mp="mtkg4h752jqg">
      <motion.h2 variants={itemVariants} className="text-xl font-black text-slate-900 text-center" data-id="element-337" data-mp="11lb5vy0tr3gs">
        {role === 'learner' ? 'In welke groep zit je?' : role === 'teacher' ? 'Over jouw school' : 'Nog een paar details'}
      </motion.h2>

      {/* Learner: Grade selection */}
      {role === 'learner' && <motion.div variants={itemVariants} className="grid grid-cols-3 gap-3" data-id="element-338" data-mp="2h2pkrby235cj">
          {['6', '7', '8'].map(g => {
        const isActive = grade === g;
        const emoji = g === '6' ? '🌟' : g === '7' ? '🚀' : '🏆';
        return <motion.button key={g} type="button" onClick={() => {
          onGradeChange(g);
          setTimeout(onNext, 350);
        }} className={`py-6 rounded-2xl border-2 text-center transition-colors relative overflow-hidden ${isActive ? 'border-orange-400 bg-orange-50 text-orange-700 shadow-sm' : 'border-slate-200 bg-white text-slate-600 hover:border-orange-200 hover:bg-orange-50/50'}`} whileTap={{
          scale: 0.95
        }} animate={isActive ? {
          scale: [1, 1.1, 1.05],
          y: -4
        } : {
          scale: 1,
          y: 0
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 15
        }} data-id="element-339" data-mp="39r3o877wytvh">
                <div className="absolute -right-2 -bottom-2 text-4xl opacity-10 grayscale filter" data-id="element-340" data-mp="3vyz8cgye1fz3">
                  {emoji}
                </div>
                <div className="relative z-10" data-id="element-341" data-mp="3h9tc91uuk5vf">
                  <div className="text-2xl mb-1" data-id="element-342" data-mp="3m9611s5a3n0n">{emoji}</div>
                  <div className="text-xs font-bold opacity-60 mb-1" data-id="element-343" data-mp="3m7cra3zdcmof">Groep</div>
                  <div className="text-3xl font-black" data-id="element-344" data-mp="ds1fzb9ogzrf">{g}</div>
                </div>
              </motion.button>;
      })}
        </motion.div>}

      {/* Parent: Email */}
      {role === 'parent' && <motion.div variants={itemVariants} data-id="element-345" data-mp="238naxk513adz">
          <label className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-346" data-mp="1ycga25rhop2w">
            E-mailadres
          </label>
          <div className="relative" data-id="element-347" data-mp="2c1rwtatni0ws">
            <Input type="email" required autoFocus value={email} onChange={e => onEmailChange(e.target.value)} icon={<Mail className="w-5 h-5" data-id="element-349" data-mp="1kdzvjsndxua7" />} className="bg-slate-50 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 text-lg font-medium py-4" placeholder="naam@voorbeeld.nl" data-id="element-348" data-mp="2c4w8et9g3yxh" />
          </div>
        </motion.div>}

      {/* Teacher: Email + School */}
      {role === 'teacher' && <motion.div variants={itemVariants} className="space-y-4" data-id="element-350" data-mp="34qpd58eaxnmw">
          <div data-id="element-351" data-mp="2zsw8wkwk73p9">
            <label className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-352" data-mp="2zt5r3mbd46ke">
              E-mailadres
            </label>
            <div className="relative" data-id="element-353" data-mp="1xeg6so6ui8bk">
              <Input type="email" required autoFocus value={email} onChange={e => onEmailChange(e.target.value)} icon={<Mail className="w-5 h-5" data-id="element-355" data-mp="26e3cvnais2ay" />} className="bg-slate-50 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 text-lg font-medium py-4" placeholder="naam@school.nl" data-id="element-354" data-mp="1xhkie6mn46c9" />
            </div>
          </div>
          <div data-id="element-356" data-mp="1iqle37su83yh">
            <label className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-357" data-mp="1inz6j85y5og9">
              School
            </label>

            {/* Selected school display */}
            {schoolName && schoolSelected ? <div className="flex items-center gap-3 p-4 bg-orange-50 border-2 border-orange-200 rounded-2xl" data-id="element-358" data-mp="v0r5x3jgr3kk">
                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center" data-id="element-359" data-mp="zzc3ev7bz95b">
                  <School className="w-5 h-5 text-orange-600" data-id="element-360" data-mp="sguqef08xc7l" />
                </div>
                <div className="flex-1" data-id="element-361" data-mp="nhhtt2jkoqrt">
                  <p className="font-bold text-slate-900" data-id="element-362" data-mp="nijfe0y7svns">{schoolName}</p>
                  <p className="text-xs text-slate-500" data-id="element-363" data-mp="ihdn248gvaes">Geselecteerd</p>
                </div>
                <button type="button" onClick={() => {
            onSchoolNameChange('');
            onSchoolSearchChange('');
            onSchoolSelected(false);
          }} className="text-sm text-orange-600 hover:text-orange-700 font-bold" data-id="element-364" data-mp="eh37bzuvh5mq">
                  Wijzig
                </button>
              </div> : schoolInputMode === 'search' && existingSchools.length > 0 ? <div className="space-y-3" data-id="element-365" data-mp="3mwv8ecct7364">
                <div className="relative" data-id="element-366" data-mp="3mvty4a3makpg">
                  <Input type="text" value={schoolSearch} onChange={e => onSchoolSearchChange(e.target.value)} icon={<School className="w-5 h-5" data-id="element-368" data-mp="19mrc7t9u7rl4" />} className="bg-slate-50 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 text-lg font-medium py-4" placeholder="Zoek je school..." autoComplete="off" data-id="element-367" data-mp="3hxin3w1l645n" />
                </div>

                {/* Matching schools list */}
                <div className="bg-white border-2 border-slate-200 rounded-2xl overflow-hidden max-h-48 overflow-y-auto" data-id="element-369" data-mp="qwi2zzfmuzjx">
                  {existingSchools.filter(s => !schoolSearch.trim() || s.toLowerCase().includes(schoolSearch.toLowerCase())).map(school => <button key={school} type="button" onClick={() => {
              onSchoolNameChange(school);
              onSchoolSearchChange('');
              onSchoolSelected(true);
            }} className="w-full text-left px-4 py-3.5 hover:bg-orange-50 text-slate-700 font-medium transition-colors border-b border-slate-100 last:border-0 flex items-center gap-3" data-id="element-370" data-mp="800ahfsymvmm">
                        <School className="w-4 h-4 text-slate-400 flex-shrink-0" data-id="element-371" data-mp="cauek22ed8v9" />
                        {school}
                      </button>)}
                  {schoolSearch.trim() && existingSchools.filter(s => s.toLowerCase().includes(schoolSearch.toLowerCase())).length === 0 && <div className="px-4 py-4 text-center text-slate-500 text-sm" data-id="element-372" data-mp="h4gk7aa58chk">
                        Geen scholen gevonden voor "{schoolSearch}"
                      </div>}
                </div>

                {/* Add new school button */}
                <button type="button" onClick={() => {
            onSchoolInputModeChange('manual');
            onSchoolNameChange(schoolSearch);
          }} className="w-full text-center py-3 text-sm font-bold text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-xl transition-colors" data-id="element-373" data-mp="1cla40blytkd3">
                  School niet gevonden? Voeg handmatig toe →
                </button>
              </div> : <div className="space-y-2" data-id="element-374" data-mp="1qhf35dblxo8z">
                <div className="relative" data-id="element-375" data-mp="1ljm0uvdfdedz">
                  <Input type="text" required value={schoolName} onChange={e => onSchoolNameChange(e.target.value)} icon={<School className="w-5 h-5" data-id="element-377" data-mp="a35fklf6w3j7" />} className="bg-slate-50 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 text-lg font-medium py-4" placeholder="bijv. Basisschool De Regenboog" autoComplete="off" autoFocus data-id="element-376" data-mp="1likehuqyl5gy" />
                </div>

                {/* Sprint 2.6 — fuzzy-match suggestion to prevent duplicates like
                    "obs de vlieger" vs "De Vlieger". */}
                {(() => {
            const suggestion = findSchoolSuggestion(schoolName, existingSchools);
            if (!suggestion) return null;
            return <motion.button type="button" initial={{
              opacity: 0,
              y: -4
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.2
            }} onClick={() => {
              onSchoolNameChange(suggestion);
              onSchoolSelected(true);
            }} className="w-full flex items-start gap-3 p-3 bg-orange-50 border border-orange-200 rounded-xl text-left hover:bg-orange-100 transition-colors" data-id="element-378" data-mp="39j7jjocgy4rq">
                      <School className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" data-id="element-379" data-mp="1v0wbpcwjjv58" />
                      <div className="flex-1 min-w-0" data-id="element-380" data-mp="1r02nsjj3mgcc">
                        <p className="text-xs font-semibold text-orange-700" data-id="element-381" data-mp="1qzkjr0q72xtv">
                          Bedoel je deze school?
                        </p>
                        <p className="text-sm font-bold text-slate-900 truncate" data-id="element-382" data-mp="1zrmjsxwcqc3m">
                          {suggestion}
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-383" data-mp="2epn8b6uwhlnq">
                          Tik om deze te gebruiken — voorkomt dubbele scholen.
                        </p>
                      </div>
                    </motion.button>;
          })()}

                {existingSchools.length > 0 && <button type="button" onClick={() => {
            onSchoolInputModeChange('search');
            onSchoolNameChange('');
            onSchoolSearchChange('');
            onSchoolSelected(false);
          }} className="text-sm font-bold text-slate-500 hover:text-orange-600 transition-colors" data-id="element-384" data-mp="2je6idqkj8l65">
                    ← Terug naar zoeken
                  </button>}
              </div>}
          </div>
        </motion.div>}

      {/* Next button for non-learner (learner auto-advances on grade pick) */}
      {role !== 'learner' && <motion.div variants={itemVariants} data-id="element-385" data-mp="1vove3z3z48n9">
          <Button type="submit" variant="primary" size="lg" className="w-full" disabled={role === 'teacher' ? !email.trim() || !schoolName.trim() : !email.trim()} data-id="element-386" data-mp="1vntxzg83ovyo">
            Verder <ChevronRight className="w-5 h-5 ml-1" data-id="element-387" data-mp="2p7lmmzi353ch" />
          </Button>
        </motion.div>}
    </motion.div>;
}